import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { client, OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { ActionCard } from '../../../ActionCard';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { sponsoredTaskCopy } from '../../../businessRelationshipNotices/copy';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { getPharmacyOptionsFromSuggestionsList } from '../../utils';
import { Step, updatedStepsOnSelect } from '../../actions/shared/helpers';
import {
  RadioLabelAndSubheader,
  RadioOptionWithSubheader,
  RadioOptionWithSubheaderWrapper,
} from '../../actions/shared/styles';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

/**
 * Dedicated task view for Select cash price next steps (OFFERED_BEST_CASH_PRICE).
 */
const OfferedBestCashPriceTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';
  const cashPricePharmacySuggestions = task.cash_price_pharmacy_suggestions ?? [];

  const pharmacyOptions = getPharmacyOptionsFromSuggestionsList(cashPricePharmacySuggestions);
  const steps: Step[] = [];

  if (pharmacyOptions.cheapestPharmacy) {
    steps.push({
      label: 'Continue with the best available cash price',
      subheader: `Tandem will send the prescription to ${pharmacyOptions.cheapestPharmacy.pharmacy_name} at $${pharmacyOptions.cheapestPharmacy.price} and inform the patient`,
      value: 'approve_cash_price',
      checked: true,
    });
  } else {
    steps.push({
      label: 'Continue with the best available cash price',
      subheader: 'Tandem will send the prescription to pharmacy and inform the patient',
      value: 'approve_cash_price',
      checked: true,
    });
  }
  if (pharmacyOptions.preferredPharmacy) {
    steps.push({
      label: "Continue with the patient's preferred pharmacy",
      subheader: `Tandem will send the prescription to ${pharmacyOptions.preferredPharmacy.pharmacy_name} at $${pharmacyOptions.preferredPharmacy.price} and inform the patient`,
      value: 'approve_cash_price_preferred_pharmacy',
      checked: false,
    });
  }

  const [selectedOption, setSelectedOption] = useState<Step[]>(steps);
  const hasSingleOption = steps.length === 1;

  const handleTaskResponse = useMutation({
    mutationFn: async (response: string) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "${response}" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('task_response', response);
      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const initialComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      if (lastTimelineItem?.item_type === 'event') {
        completionEvent = lastTimelineItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for Offered Best Cash Price task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const handleContinue = () => {
    const selectedValue = selectedOption.find((option) => option.checked)?.value;
    handleTaskResponse.mutate(selectedValue ?? 'approve_cash_price');
  };

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        <div className={`mb-6 flex flex-col gap-4 ${isCompleted ? 'border-b-grayscale-100 pb-4 border-b-solid' : ''}`}>
          <TaskInstructionSummary>
            Please confirm authorization for Tandem to continue with best available cash price:
          </TaskInstructionSummary>
          {initialComment && (
            <Typography styledAs="bodyExtraSmallDMSans">
              <RenderHtmlString>{initialComment.message}</RenderHtmlString>
            </Typography>
          )}
        </div>

        {!isCompleted && (
          <ActionCard>
            {hasSingleOption ? (
              <div className="mb-2 flex flex-col gap-3">
                <Typography styledAs="bodySmallDMSans" weight={600}>
                  {steps[0].label}
                </Typography>
                {steps[0].subheader && (
                  <Typography styledAs="bodySmallDMSans" weight={400} color="var(--grayscale-600)">
                    {steps[0].subheader}
                  </Typography>
                )}
              </div>
            ) : (
              <RadioBlockGroup $variant="outlined">
                {selectedOption.map((step) =>
                  step.subheader ? (
                    <RadioOptionWithSubheaderWrapper key={step.value}>
                      <RadioOptionWrapper
                        id={step.value}
                        name="cash-price-options"
                        value={step.value}
                        checked={step.checked}
                        onChange={() => setSelectedOption(updatedStepsOnSelect(selectedOption, step))}
                        variant="outlined"
                        disabled={handleTaskResponse.isPending}
                      >
                        <RadioOptionWithSubheader>
                          <RadioLabelAndSubheader>
                            <RadioLabel>{step.label}</RadioLabel>
                            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                              {step.subheader}
                            </Typography>
                          </RadioLabelAndSubheader>
                        </RadioOptionWithSubheader>
                      </RadioOptionWrapper>
                    </RadioOptionWithSubheaderWrapper>
                  ) : (
                    <RadioOptionWrapper
                      key={step.value}
                      id={step.value}
                      name="cash-price-options"
                      value={step.value}
                      checked={step.checked}
                      onChange={() => setSelectedOption(updatedStepsOnSelect(selectedOption, step))}
                      variant="outlined"
                      disabled={handleTaskResponse.isPending}
                    >
                      <RadioLabel>{step.label}</RadioLabel>
                    </RadioOptionWrapper>
                  ),
                )}
              </RadioBlockGroup>
            )}
            <ButtonRow
              align={Alignment.SpaceBetween}
              fullWidth
              className={hasSingleOption ? 'border-t-grayscale-100 pt-4 border-t-solid' : undefined}
            >
              <ButtonRow align={Alignment.Left}>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setShowDeclineModal(true)}
                  disabled={handleTaskResponse.isPending}
                >
                  Cancel prescription
                </Button>
                <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
              </ButtonRow>
              <Button
                variant="primary"
                size="sm"
                onClick={handleContinue}
                disabled={handleTaskResponse.isPending}
                isLoading={handleTaskResponse.isPending}
              >
                {hasSingleOption ? 'Confirm' : 'Continue'}
              </Button>
            </ButtonRow>
          </ActionCard>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default OfferedBestCashPriceTaskView;
